/**
 * Unified error handler for microservices
 * Handles different error response formats from Go services
 */

export class ServiceError extends Error {
  constructor(
    message: string,
    public service: 'signup' | 'company' | 'candidate' | 'david-candidate',
    public code: string,
    public statusCode: number
  ) {
    super(message);
    this.name = 'ServiceError';
  }
}

/**
 * Handle API errors and convert them to ServiceError
 * @param error - The error object from axios or fetch
 * @param service - The service name (signup, company, candidate, david-candidate)
 * @returns ServiceError with normalized error information
 */
export function handleApiError(error: any, service: 'signup' | 'company' | 'candidate' | 'david-candidate'): ServiceError {
  // Network error (service unreachable)
  if (!error.response) {
    return new ServiceError(
      `${service} service is unreachable`,
      service,
      'NETWORK_ERROR',
      0
    );
  }

  const status = error.response.status;
  const data = error.response.data;

  // Extract error message based on service format
  let message = 'An error occurred';
  let code = 'UNKNOWN_ERROR';

  if (data.status?.msg) {
    // Standard format (Signup, Company)
    message = data.status.msg;
    code = data.error || 'ERROR_SERVER';
  } else if (data.error && data.message) {
    // Session format (Candidate)
    message = data.message;
    code = data.error;
  } else if (data.error) {
    message = data.error;
    code = data.error;
  } else if (data.message) {
    message = data.message;
  }

  return new ServiceError(message, service, code, status);
}

/**
 * Get user-friendly error message based on error code
 * @param error - The ServiceError
 * @returns User-friendly error message
 */
export function getUserFriendlyMessage(error: ServiceError): string {
  const errorMessages: Record<string, string> = {
    // Signup service errors
    ERROR_VALIDATION: 'Please check your input and try again',
    ERROR_UNAUTHORIZED: 'Invalid credentials. Please try again',
    ERROR_NOT_FOUND: 'Resource not found',
    ERROR_EXISTS: 'This email is already registered',
    ERROR_SERVER: 'Server error. Please try again later',

    // Candidate service errors
    MISSING_FILE: 'Please select a resume file to upload',
    UPLOAD_FAILED: 'Failed to upload resume. Please try again',
    INVALID_REQUEST: 'Invalid request. Please check your input',
    ANSWER_FAILED: 'Failed to save answer. Please try again',
    SESSION_NOT_READY: 'Please answer all required questions before completing',
    SESSION_NOT_FOUND: 'Session not found or has expired',
    ACTIVE_SESSION_EXISTS: 'You have an active session. Please complete or cancel it first',

    // David AI Candidate service errors
    CONVERSATION_ALREADY_COMPLETED: 'This conversation has already been completed',
    CONVERSATION_NOT_FOUND: 'Conversation session not found',
    CANDIDATE_NOT_FOUND: 'Candidate profile not found',
    EMPTY_MESSAGE: 'Message cannot be empty',
    LLM_ERROR: 'AI service temporarily unavailable. Please try again',
    DATABASE_ERROR: 'Database error. Please try again later',

    // Network errors
    NETWORK_ERROR: 'Unable to connect to server. Please check your internet connection',
  };

  return errorMessages[error.code] || error.message || 'An unexpected error occurred';
}
